import React, { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/system";
import { Link, useLocation } from "react-router-dom";
import MobileMenu from "./MobileMenu";
import { Images } from "../../../utils/Images";

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isProfilePage',
})(({ theme, isProfilePage }) => ({
  backgroundColor: "#FFFFFF",
  boxShadow: "none",
  padding: "12px 0",
  marginBottom: isProfilePage ? "0px" : "0px",
}));

const StyledButton = styled(Button)(({ active }) => ({
  textTransform: "capitalize",
  // fontFamily: "Red Hat Display",
  fontSize: "14px",
  fontWeight:active ? 600 : 500,
  fontStyle: "normal",
  color: active ? "#EC1B26 !important" : "#151515 !important",
  letterSpacing: "-0.02em",
  lineHeight: "16px",
  padding: "4px 8px",
  minWidth: "auto",
  backgroundColor: "transparent !important",
  boxShadow: "none",
  border: "none",
  outline: "none",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "transparent !important",
    color: "#EC1B26 !important",
    boxShadow: "none",
  },
  "&:focus": {
    backgroundColor: "transparent !important",
    boxShadow: "none",
    outline: "none",
    color: active ? "#EC1B26 !important" : "#151515 !important",
  },
  "&:active": {
    backgroundColor: "transparent !important",
    boxShadow: "none",
    color: active ? "#EC1B26 !important" : "#151515 !important",
  },
  "&:visited": {
    color: active ? "#EC1B26 !important" : "#151515 !important",
  },
  "&:link": {
    color: active ? "#EC1B26 !important" : "#151515 !important",
  },
}));

const NavigationBar = () => {
  const [activeMenuItem, setActiveMenuItem] = useState("Home");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  
  // Check if current route is a profile page
  const isProfilePage = location.pathname.startsWith('/profile');

  const menuItems = [
    { label: "Home", path: "/" },
    { label: "Life", path: "/life" },
    { label: "Explore Campaigns", path: "/campaigns" },
    { label: "Monthly Donation", path: "/monthly-donation" },
    { label: "Small Vendors", path: "/small-vendors" },
    { label: "Temples", path: "/temples" },
    { label: "Start New Campaign", path: "/start-campaign" },
    { label: "Blogs", path: "/blogs" },
    { label: "Contact Us", path: "/contact" },
    { label: "About Us", path: "/about" },
  ];

  // Update active menu item based on current location
  React.useEffect(() => {
    const currentItem = menuItems.find(
      (item) => item.path === location.pathname
    );
    if (currentItem) {
      setActiveMenuItem(currentItem.label);
    }
  }, [location.pathname]);

  const handleMenuItemClick = (label) => {
    setActiveMenuItem(label);
  };

  return (
    <StyledAppBar position="static" isProfilePage={isProfilePage}>
      <Container maxWidth={false} disableGutters sx={{ width: "94.44%" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box
            component={Link}
            to="/"
            sx={{
              textDecoration: "none",
              display: "block",
            }}
          >
            <Box
              component="img"
              src={Images.logo}
              alt="Logo"
              sx={{
                width: { md: "140px", lg: "156px" },
                height: "42px",
                objectFit: "contain",
                imageRendering: "auto",
                WebkitImageRendering: "-webkit-optimize-contrast",
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                willChange: "transform",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
                WebkitTransform: "translateZ(0)",
              }}
            />
          </Box>

          {isMobile ? (
            <MobileMenu
              menuItems={menuItems}
              activeMenuItem={activeMenuItem}
              handleMenuItemClick={handleMenuItemClick}
            />
          ) : (
            <Stack
              direction="row"
              spacing={{ xs: "16px", md: "0px", lg: "16px" }}
              alignItems="center"
              component="nav"
              aria-label="main navigation"
            >
              {menuItems.map((item) => (
                <StyledButton
                  key={item.label}
                  component={Link}
                  to={item.path}
                  active={activeMenuItem === item.label}
                  onClick={() => setActiveMenuItem(item.label)}
                  disableRipple
                  sx={{
                    textDecoration: "none",
                    "&.MuiLink-root": {
                      color: "inherit",
                    },
                    "&.MuiLink-root:hover": {
                      color: "inherit",
                    },
                    "&.MuiLink-root:visited": {
                      color: "inherit",
                    },
                    "&.MuiLink-root:link": {
                      color: "inherit",
                    },
                  }}
                >
                  {item.label}
                </StyledButton>
              ))}
            </Stack>
          )}
        </Box>
      </Container>
    </StyledAppBar>
  );
};

export default NavigationBar;
